import { supabase } from '../lib/supabase';

export interface SupportTicketCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  displayOrder: number;
  isActive: boolean;
}

export interface SupportTicket {
  id: string;
  ticketNumber: string;
  tenantId: string | null;
  userId: string;
  userEmail: string;
  userName: string | null;
  categoryId: string | null;
  categoryName?: string;
  categoryIcon?: string;
  subject: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  lastMessageAt: string;
  lastMessageBy: 'user' | 'admin';
  hasUnreadAdminReply: boolean;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  tenantName?: string;
}

export interface SupportTicketMessage {
  id: string;
  ticketId: string;
  senderType: 'user' | 'admin';
  senderId: string;
  senderName: string | null;
  content: string;
  isInternalNote: boolean;
  createdAt: string;
}

export interface CreateTicketParams {
  tenantId: string | null;
  userId: string;
  userEmail: string;
  userName: string | null;
  categoryId: string;
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface TicketFilters {
  status?: string;
  categoryId?: string;
  search?: string;
  tenantId?: string;
}

export interface TicketStats {
  totalOpen: number;
  inProgress: number;
  resolvedToday: number;
  totalTickets: number;
}

export async function fetchCategories(): Promise<SupportTicketCategory[]> {
  const { data, error } = await supabase
    .from('support_ticket_categories')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch categories: ${error.message}`);
  }

  return (data || []).map(c => ({
    id: c.id,
    name: c.name,
    description: c.description,
    icon: c.icon,
    displayOrder: c.display_order,
    isActive: c.is_active
  }));
}

export interface CreateTicketResult {
  ticket: SupportTicket;
  initialMessageId: string;
}

export async function createTicket(params: CreateTicketParams): Promise<CreateTicketResult> {
  const { data: ticketData, error: ticketError } = await supabase
    .from('support_tickets')
    .insert({
      tenant_id: params.tenantId,
      user_id: params.userId,
      user_email: params.userEmail,
      user_name: params.userName,
      category_id: params.categoryId,
      subject: params.subject,
      status: 'open',
      priority: params.priority || 'medium',
      ticket_number: ''
    })
    .select('*')
    .single();

  if (ticketError) {
    throw new Error(`Failed to create ticket: ${ticketError.message}`);
  }

  const { data: messageData, error: messageError } = await supabase
    .from('support_ticket_messages')
    .insert({
      ticket_id: ticketData.id,
      sender_type: 'user',
      sender_id: params.userId,
      sender_name: params.userName || params.userEmail,
      content: params.description,
      is_internal_note: false
    })
    .select('id')
    .single();

  if (messageError) {
    throw new Error(`Failed to create initial message: ${messageError.message}`);
  }

  return {
    ticket: {
      id: ticketData.id,
      ticketNumber: ticketData.ticket_number,
      tenantId: ticketData.tenant_id,
      userId: ticketData.user_id,
      userEmail: ticketData.user_email,
      userName: ticketData.user_name,
      categoryId: ticketData.category_id,
      subject: ticketData.subject,
      status: ticketData.status,
      priority: ticketData.priority,
      lastMessageAt: ticketData.last_message_at,
      lastMessageBy: ticketData.last_message_by,
      hasUnreadAdminReply: ticketData.has_unread_admin_reply,
      createdAt: ticketData.created_at,
      updatedAt: ticketData.updated_at,
      resolvedAt: ticketData.resolved_at
    },
    initialMessageId: messageData.id
  };
}

export async function fetchUserTickets(
  userId: string,
  page: number = 1,
  pageSize: number = 20
): Promise<{ tickets: SupportTicket[]; total: number }> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from('support_tickets')
    .select(`
      *,
      support_ticket_categories (
        name,
        icon
      )
    `, { count: 'exact' })
    .eq('user_id', userId)
    .order('last_message_at', { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(`Failed to fetch tickets: ${error.message}`);
  }

  const tickets: SupportTicket[] = (data || []).map(t => ({
    id: t.id,
    ticketNumber: t.ticket_number,
    tenantId: t.tenant_id,
    userId: t.user_id,
    userEmail: t.user_email,
    userName: t.user_name,
    categoryId: t.category_id,
    categoryName: t.support_ticket_categories?.name,
    categoryIcon: t.support_ticket_categories?.icon,
    subject: t.subject,
    status: t.status,
    priority: t.priority,
    lastMessageAt: t.last_message_at,
    lastMessageBy: t.last_message_by,
    hasUnreadAdminReply: t.has_unread_admin_reply,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    resolvedAt: t.resolved_at
  }));

  return { tickets, total: count || 0 };
}

export async function fetchTicketWithMessages(
  ticketId: string
): Promise<{ ticket: SupportTicket; messages: SupportTicketMessage[] } | null> {
  const { data: ticketData, error: ticketError } = await supabase
    .from('support_tickets')
    .select(`
      *,
      support_ticket_categories (
        name,
        icon
      ),
      tenants (
        name
      )
    `)
    .eq('id', ticketId)
    .maybeSingle();

  if (ticketError) {
    throw new Error(`Failed to fetch ticket: ${ticketError.message}`);
  }

  if (!ticketData) {
    return null;
  }

  const { data: messagesData, error: messagesError } = await supabase
    .from('support_ticket_messages')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });

  if (messagesError) {
    throw new Error(`Failed to fetch messages: ${messagesError.message}`);
  }

  const ticket: SupportTicket = {
    id: ticketData.id,
    ticketNumber: ticketData.ticket_number,
    tenantId: ticketData.tenant_id,
    userId: ticketData.user_id,
    userEmail: ticketData.user_email,
    userName: ticketData.user_name,
    categoryId: ticketData.category_id,
    categoryName: ticketData.support_ticket_categories?.name,
    categoryIcon: ticketData.support_ticket_categories?.icon,
    subject: ticketData.subject,
    status: ticketData.status,
    priority: ticketData.priority,
    lastMessageAt: ticketData.last_message_at,
    lastMessageBy: ticketData.last_message_by,
    hasUnreadAdminReply: ticketData.has_unread_admin_reply,
    createdAt: ticketData.created_at,
    updatedAt: ticketData.updated_at,
    resolvedAt: ticketData.resolved_at,
    tenantName: ticketData.tenants?.name
  };

  const messages: SupportTicketMessage[] = (messagesData || []).map(m => ({
    id: m.id,
    ticketId: m.ticket_id,
    senderType: m.sender_type,
    senderId: m.sender_id,
    senderName: m.sender_name,
    content: m.content,
    isInternalNote: m.is_internal_note,
    createdAt: m.created_at
  }));

  return { ticket, messages };
}

export async function addMessage(
  ticketId: string,
  senderId: string,
  senderName: string,
  senderType: 'user' | 'admin',
  content: string,
  isInternalNote: boolean = false
): Promise<SupportTicketMessage> {
  const { data, error } = await supabase
    .from('support_ticket_messages')
    .insert({
      ticket_id: ticketId,
      sender_type: senderType,
      sender_id: senderId,
      sender_name: senderName,
      content,
      is_internal_note: isInternalNote
    })
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to add message: ${error.message}`);
  }

  return {
    id: data.id,
    ticketId: data.ticket_id,
    senderType: data.sender_type,
    senderId: data.sender_id,
    senderName: data.sender_name,
    content: data.content,
    isInternalNote: data.is_internal_note,
    createdAt: data.created_at
  };
}

export async function markTicketAsRead(ticketId: string): Promise<void> {
  const { error } = await supabase
    .from('support_tickets')
    .update({ has_unread_admin_reply: false })
    .eq('id', ticketId);

  if (error) {
    throw new Error(`Failed to mark ticket as read: ${error.message}`);
  }
}

export async function fetchAllTickets(
  filters?: TicketFilters,
  page: number = 1,
  pageSize: number = 20
): Promise<{ tickets: SupportTicket[]; total: number }> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('support_tickets')
    .select(`
      *,
      support_ticket_categories (
        name,
        icon
      ),
      tenants (
        name
      )
    `, { count: 'exact' })
    .order('last_message_at', { ascending: false });

  if (filters?.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters?.categoryId) {
    query = query.eq('category_id', filters.categoryId);
  }

  if (filters?.tenantId) {
    query = query.eq('tenant_id', filters.tenantId);
  }

  if (filters?.search) {
    query = query.or(`subject.ilike.%${filters.search}%,ticket_number.ilike.%${filters.search}%,user_email.ilike.%${filters.search}%`);
  }

  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch tickets: ${error.message}`);
  }

  const tickets: SupportTicket[] = (data || []).map(t => ({
    id: t.id,
    ticketNumber: t.ticket_number,
    tenantId: t.tenant_id,
    userId: t.user_id,
    userEmail: t.user_email,
    userName: t.user_name,
    categoryId: t.category_id,
    categoryName: t.support_ticket_categories?.name,
    categoryIcon: t.support_ticket_categories?.icon,
    subject: t.subject,
    status: t.status,
    priority: t.priority,
    lastMessageAt: t.last_message_at,
    lastMessageBy: t.last_message_by,
    hasUnreadAdminReply: t.has_unread_admin_reply,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    resolvedAt: t.resolved_at,
    tenantName: t.tenants?.name
  }));

  return { tickets, total: count || 0 };
}

export async function updateTicketStatus(
  ticketId: string,
  status: 'open' | 'in_progress' | 'resolved' | 'closed'
): Promise<void> {
  const updateData: Record<string, unknown> = {
    status,
    updated_at: new Date().toISOString()
  };

  if (status === 'resolved' || status === 'closed') {
    updateData.resolved_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from('support_tickets')
    .update(updateData)
    .eq('id', ticketId);

  if (error) {
    throw new Error(`Failed to update ticket status: ${error.message}`);
  }
}

export async function updateTicketPriority(
  ticketId: string,
  priority: 'low' | 'medium' | 'high' | 'urgent'
): Promise<void> {
  const { error } = await supabase
    .from('support_tickets')
    .update({
      priority,
      updated_at: new Date().toISOString()
    })
    .eq('id', ticketId);

  if (error) {
    throw new Error(`Failed to update ticket priority: ${error.message}`);
  }
}

export async function fetchTicketStats(): Promise<TicketStats> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data: allTickets, error: allError } = await supabase
    .from('support_tickets')
    .select('status, resolved_at', { count: 'exact' });

  if (allError) {
    throw new Error(`Failed to fetch ticket stats: ${allError.message}`);
  }

  const tickets = allTickets || [];
  const totalOpen = tickets.filter(t => t.status === 'open').length;
  const inProgress = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedToday = tickets.filter(t =>
    t.resolved_at && new Date(t.resolved_at) >= today
  ).length;

  return {
    totalOpen,
    inProgress,
    resolvedToday,
    totalTickets: tickets.length
  };
}

export async function getUnreadTicketCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('has_unread_admin_reply', true);

  if (error) {
    throw new Error(`Failed to fetch unread count: ${error.message}`);
  }

  return count || 0;
}

export async function getOpenTicketCount(): Promise<number> {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('*', { count: 'exact', head: true })
    .in('status', ['open', 'in_progress']);

  if (error) {
    throw new Error(`Failed to fetch open ticket count: ${error.message}`);
  }

  return count || 0;
}

export async function fetchTicketsByTenant(
  tenantId: string,
  page: number = 1,
  pageSize: number = 20
): Promise<{ tickets: SupportTicket[]; total: number }> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await supabase
    .from('support_tickets')
    .select(`
      *,
      support_ticket_categories (
        name,
        icon
      )
    `, { count: 'exact' })
    .eq('tenant_id', tenantId)
    .order('last_message_at', { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(`Failed to fetch tenant tickets: ${error.message}`);
  }

  const tickets: SupportTicket[] = (data || []).map(t => ({
    id: t.id,
    ticketNumber: t.ticket_number,
    tenantId: t.tenant_id,
    userId: t.user_id,
    userEmail: t.user_email,
    userName: t.user_name,
    categoryId: t.category_id,
    categoryName: t.support_ticket_categories?.name,
    categoryIcon: t.support_ticket_categories?.icon,
    subject: t.subject,
    status: t.status,
    priority: t.priority,
    lastMessageAt: t.last_message_at,
    lastMessageBy: t.last_message_by,
    hasUnreadAdminReply: t.has_unread_admin_reply,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    resolvedAt: t.resolved_at
  }));

  return { tickets, total: count || 0 };
}

export async function getUnreadTicketCountByTenant(tenantId: string): Promise<number> {
  const { count, error } = await supabase
    .from('support_tickets')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('has_unread_admin_reply', true);

  if (error) {
    throw new Error(`Failed to fetch tenant unread count: ${error.message}`);
  }

  return count || 0;
}
